import { CmsImageData, WidgetTab } from '../common';
import { DEFAULT_DATE_I18N, DEFAULT_NUMBER_I18N, DotNestedKeys } from './types';

// == Component translations ===================================================

export const continueButtonI18n = {
  search: 'Search',
  ariaLabel: 'Continue booking with current selection',
};
export type ContinueButtonI18n = typeof continueButtonI18n;

export const dateSelectorI18n = {
  departure: {
    title: 'Departure',
    placeholder: 'Departure date',
  },
  return: {
    title: 'Return',
    placeholder: 'Return date',
  },
};
export type DateSelectorI18n = typeof dateSelectorI18n;

export const dateModalI18n = {
  dateFormatDescription: 'Enter the date in the following format: day period month period year.',
  inputPlaceholder: 'dd.mm.yyyy',
  invalidDate: 'Invalid date',
  reset: 'Reset',
  resetTravelDates: 'Reset travel dates',
  selectDeparture: 'Select departure date',
  selectReturn: 'Select return date',
  title: 'Select travel dates',
  flexibleDates: 'Dates are flexible',
  directFlights: 'Direct flights',
  directFlightsOnly: 'direct flights only',
};
export type DateModalI18n = typeof dateModalI18n;

export const locationSelectorI18n = {
  allAirportsLabel: 'All',
  origin: {
    title: 'From',
    placeholder: 'Travelling from',
  },
  destination: {
    title: 'To',
    placeholder: 'Travelling to',
  },
};
export type LocationSelectorI18n = typeof locationSelectorI18n;

export const locationModalI18n = {
  origin: {
    inputPlaceholder: 'Type and select departure',
    nearestAiport: 'Airports near you',
    title: 'Flight from',
    listTitle: 'Origin locations',
    results: {
      one: 'Found {{count}} location.',
      many: 'Found {{count}} locations.',
      none: 'No results found for {{queryString}}',
    },
  },
  destination: {
    inputPlaceholder: 'Type and select destination',
    title: 'Flight to',
    listTitle: 'Destination locations',
    results: {
      one: 'Found {{count}} destination.',
      many: 'Found {{count}} destinations.',
      none: 'No results found for {{queryString}}',
    },
  },
  locateMe: 'Locate me',
  trending: 'Trending now',
  previousSearches: 'Your previous searches',
  aria: {
    airport: 'Country {{country}}, {{type}} {{title}}, location code {{locationCode}}.',
    city: 'Country {{country}}, {{type}} {{title}}, location code {{locationCode}}.',
    state: 'Country {{country}}, {{type}} {{title}}, location code {{locationCode}}.', // TODO: Check if necessary
  },
  selectionKeys: 'Use arrow keys UP and DOWN for moving in the list of places and Enter for selecting one.',
};
export type LocationModalI18n = typeof locationModalI18n;

export const locationSwitcherI18n = {
  ariaLabel: 'Switch locations',
};
export type LocationSwitcherI18n = typeof locationSwitcherI18n;

export const paxAndClassSelectorI18n = {
  label: 'Passengers and class',
};
export type PaxAndClassSelectorI18n = typeof paxAndClassSelectorI18n;

export const paxAmountSelectorI18n = {
  title: 'Passengers',
};
export type PaxAmountSelectorI18n = typeof paxAmountSelectorI18n;

export const paxAmountModalI18n = {
  title: 'Passengers',
  age: {
    adults: undefined as string,
    children: '2-11',
    c15s: '12-15',
    infants: '0-2',
  },
  ageRule: {
    adults: undefined as string,
    children: 'Age upon departure',
    c15s: 'Age upon departure',
    infants: 'Age upon return',
  },
  add: {
    adults: 'Add an adult',
    children: 'Add a child',
    c15s: 'Add a child',
    infants: 'Add an infant',
  },
  remove: {
    adults: 'Remove an adult',
    children: 'Remove a child',
    c15s: 'Remove a child',
    infants: 'Remove an infant',
  },
};

export const paxAmountDetailsI18n = {
  sum: { one: '1 passenger', many: '{{passengerCount}} passengers' },
};

export type PaxAmountDetailsI18n = typeof paxAmountDetailsI18n;

export type PaxAmountModalI18n = typeof paxAmountModalI18n;

export const startingPriceI18n = {
  title: 'Price per adult from',
  pricesFrom: 'Prices from',
};
export type StartingPriceI18n = typeof startingPriceI18n;

export const travelClassSelectorI18n = {
  title: 'Travel classes',
};
export type TravelClassSelectorI18n = typeof travelClassSelectorI18n;

export const travelClassModalI18n = {
  title: 'Select travel class',
};
export type TravelClassModalI18n = typeof travelClassModalI18n;

export const notificationI18n = {
  busConnection:
    'Finnair operates the routes from Tampere and Turku to Helsinki Airport and back only as bus connections.',
  route: {
    one: 'Finnair flies direct from {{origin}} to {{destination}} {{start}} - {{end}}.',
    many: 'Finnair flies direct from {{origin}} to {{destination}} {{start}} - {{end}} and {{start2}} - {{end2}}.',
  },
  noFlights: 'There are no flights available for the selected dates.',
  timetable: {
    checkTimeTableText: 'Please check the timetable to see when Finnair flies this route and try again.',
    forRouteLinkText: 'Timetable for this route',
    show: 'See timetables',
    hide: 'Hide timetables',
  },
  routeNotSupported: 'Selected route not supported, please select another route',
  partnerFlights: 'Flights operated by <b>one</b>world partners might be available outside these dates.',
  multiCity: {
    noFlightsFound: 'There are no flights available for the selected dates or route.',
    seeMoreLinkText: 'See more info on multi-city bookings.',
    seeMoreLink: {
      url: '#',
    },
  },
  kuusamo:
    'Kuusamo Airport is closed for renovation on your selected dates (17 Jul-15 Aug 2023). Finnair will continue to fly to/from Kuusamo after the renovation is completed.',
};

export const bookingWidgetI18n = {
  title: 'Where would you like to fly?',
  departure: 'Departure',
  return: 'Return',
};
export type BookingWidgetI18n = typeof bookingWidgetI18n;

export interface BackgroundImageI18n {
  backgroundImage?: CmsImageData;
}

export const travelTypeI18n = {
  oneway: 'One way',
  return: 'Return',
  awardFlight: 'Award flight',
  multiCity: 'Multi city',
  aroundTheWorld: 'Around the World',
  aurinkomatkat: 'Aurinkomatkat',
  label: 'Trip type',
};
export type TravelTypeI18n = typeof travelTypeI18n;

export const travelClassI18n = {
  mixed: 'All classes',
  mixedCombined: 'All travel classes',
  economy: 'Economy',
  ecopremium: 'Premium Economy',
  business: 'Business',
};
export type TravelClassI18n = typeof travelClassI18n;

export const paxAmountI18n = {
  adults: { one: 'adult', many: 'adults', none: 'adult' },
  children: { one: 'child', many: 'children', none: 'child' },
  c15s: { one: 'child', many: 'children', none: 'child' },
  infants: { one: 'infant', many: 'infants', none: 'infant' },
};
export type PaxAmountI18n = typeof paxAmountI18n;

export const actionI18n = {
  close: 'Close',
  remove: 'Remove',
  done: 'Done',
  loading: 'Loading',
  reset: 'Reset',
  cancel: 'Cancel',
  continue: 'Continue',
};
export type ActionI18n = typeof actionI18n;

export const airCalendarI18n = {
  title: 'Check prices and select dates',
  flexibleTravelDates: 'Flexible travel dates',
  departureTitle: 'Departure',
  returnTitle: 'Return',
};
export type AirCalendarI18n = typeof airCalendarI18n;

export const aurinkomatkatI18n = {
  title: 'Varaa lento + hotelli',
  inCooperation: 'Yhteistyössä <b>Aurinkomatkojen</b> kanssa',
  paxAmount: {
    selectAge: 'Valitse ikä',
    childAgeRule: 'Ikä paluuhetkellä',
    childAgeRange: '0–17 vuotta',
    addRoom: 'Lisää huone',
    removeRoom: 'Poista huone {{roomNumber}}',
    room: 'Huone',
    year: 'vuosi',
    years: 'vuotta',
  },
  calendar: {
    recommendedDates: 'Suosittelemamme matkustuspäivät',
  },
  duration: 'Kesto',
  selectDuration: 'Valitse kesto',
  notification: {
    teaser: 'Kolminkertaiset Finnair Plus -pisteet Lento + hotelli -matkoista',
    content:
      'Varaa lento ja hotelli yhdessä ja nauti matkapakettilain tuomasta turvasta. Nyt saat matkastasi myös kolminkertaiset Finnair Plus -pisteet.',
    showMore: 'Näytä enemmän',
    showLess: 'Näytä vähemmän',
  },
  continueModal: {
    title: 'Aurinkomatkojen {{holidayType}}',
    contentTitle: 'Varauksesi jatkuu Aurinkomatkojen sivuilla',
    content:
      'Lento + hotelli -matkat tarjotaan yhteistyössä Aurinkomatkojen kanssa. Palvelu on saatavissa vain suomeksi.',
  },
  priceText: 'per aikuinen',
};

export type AurinkomatkatI18n = typeof aurinkomatkatI18n;

export type TabI18n = { tabs: { [key in WidgetTab]: string }; next: string; previous: string };
export const tabI18n: TabI18n = {
  tabs: {
    flight: 'Flight',
    award: 'Award Flight',
    aurinkomatkat: 'Flight + Hotel',
  },
  next: 'Next',
  previous: 'Previous',
};

export const previousSearchesI18n = {
  title: 'Your previous searches:',
};

export type AwardI18n = {
  login: {
    title: string;
    description: string;
    login: string;
    join: string;
  };
  moveUpToFinnairPlus: {
    title: string;
    description: string;
    signup: string;
  };
  corporate: {
    title: string;
    description: string;
    login: string;
    signup: string;
  };
  notifications: {
    totalPoints: string;
  };
  pointsAmount: string;
};
export const awardI18n: AwardI18n = {
  login: {
    title: 'Booking award flight requires Finnair Plus account',
    description: 'You can use your Finnair Plus points to purchase Finnair Plus award flights.',
    login: 'Log in',
    join: 'Join Finnair Plus',
  },
  moveUpToFinnairPlus: {
    title: 'You will need a Finnair Plus account',
    description: 'Join Finnair Plus to start collecting points which you can then use on award flights.',
    signup: 'Sign up for free',
  },
  corporate: {
    title: "Corporate customers can't book award flights",
    description: 'You are logged in with a corporate account. Switch to your Finnair Plus account or join now.',
    login: 'Log in as a Finnair Plus member',
    signup: 'Sign up for free',
  },
  notifications: {
    totalPoints: 'You have {{points}} Finnair plus points',
  },
  pointsAmount: 'points',
};

export const promoCodeI18n = {
  emptyButton: 'Use a discount code',
  buttonWithCode: 'Discount code: {{code}}',
  modalTitle: 'Discount code',
  loginWarning:
    'Make sure to check the terms and conditions of the discount code you have received. Some codes only work if you are logged in.',
  inputLabel: 'Enter code',
  inputValidationError: 'The entered code is invalid. Try adding it exactly as you received it.',
  removeCodeButton: 'Remove code',
  promoLinkText: 'See terms',
  termsLink: {
    url: '#',
  },
  checkCodeButton: 'Check code',
  termsTitle: 'Use these rules for your flight search',
  notFoundError: "The code you entered doesn't exist",
  genericError: 'Please edit and try again. Find more information on using discount codes via the link below.',
};

export type PromoCodeI18n = typeof promoCodeI18n;

export const multiCityTooltipI18n = {
  description:
    'Book a return trip for different routes in each direction. You must return from the same continent where you flew to, and to the same continet where you left from.',
  linkText: 'FAQ',
  link: {
    url: {
      url: '#',
    },
  },
};

export type MultiCityTooltipI18n = typeof multiCityTooltipI18n;

// == Combine localizations ====================================================

export const localizations = {
  continueButton: continueButtonI18n,
  startingPrice: startingPriceI18n,
  travelType: travelTypeI18n,
  travelClass: travelClassI18n,
  travelClassSelector: travelClassSelectorI18n,
  travelClassModal: travelClassModalI18n,
  locationSelector: locationSelectorI18n,
  locationModal: locationModalI18n,
  locationSwitcher: locationSwitcherI18n,
  date: DEFAULT_DATE_I18N,
  dateSelector: dateSelectorI18n,
  dateModal: dateModalI18n,
  paxAmount: paxAmountI18n,
  paxAmountSelector: paxAmountSelectorI18n,
  paxAmountModal: paxAmountModalI18n,
  paxAmountDetails: paxAmountDetailsI18n,
  paxAndClassSelector: paxAndClassSelectorI18n,
  number: DEFAULT_NUMBER_I18N,
  notification: notificationI18n,
  airCalendar: airCalendarI18n,
  action: actionI18n,
  bookingWidget: bookingWidgetI18n,
  aurinkomatkat: aurinkomatkatI18n,
  bookingWidgetTabs: tabI18n,
  previousSearches: previousSearchesI18n,
  award: awardI18n,
  promoCode: promoCodeI18n,
  multiCityTooltip: multiCityTooltipI18n,
};

export interface DynamicNotifications {
  dynamicNotifications?: {
    destination?: {
      [countryCode: string]: string;
    };
  };
}

export type Localizations = typeof localizations & BackgroundImageI18n & DynamicNotifications;
export type KeysOfLocalizations = DotNestedKeys<Localizations & AurinkomatkatI18n>;
